import React from 'react';
import { Command } from 'cmdk';
import {
  PlusOutlined,
  MessageOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { AgentConversationSummary } from '../../types/agent';
import styles from './AgentCommandPalette.module.css';

interface AgentCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversations: AgentConversationSummary[];
  onSelectConversation: (id: number) => void;
  onRunPrompt: (prompt: string) => void;
  onNewChat: () => void;
}

// Slash-style quick prompts for power users. Exported so the global ⌘K palette
// can surface the same assistant commands in one unified field.
export const SLASH_COMMANDS: { keyword: string; label: string; prompt: string }[] = [
  {
    keyword: '/summarize',
    label: 'Підсумувати замовлення',
    prompt: 'Підсумуй сьогоднішні замовлення',
  },
  {
    keyword: '/analyze',
    label: 'Проаналізувати залишки',
    prompt: 'Проаналізуй залишки на складі та познач критичні',
  },
  {
    keyword: '/report',
    label: 'Звіт за тиждень',
    prompt: 'Згенеруй звіт по продажах за останній тиждень',
  },
];

const AgentCommandPalette: React.FC<AgentCommandPaletteProps> = ({
  open,
  onOpenChange,
  conversations = [],
  onSelectConversation,
  onRunPrompt,
  onNewChat,
}) => {
  const close = () => onOpenChange(false);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Команди асистента"
      overlayClassName={styles.overlay}
      contentClassName={styles.content}
    >
      <Command.Input className={styles.input} placeholder="Пошук розмов або /команди…" />
      <Command.List className={styles.list}>
        <Command.Empty className={styles.empty}>Нічого не знайдено</Command.Empty>

        <Command.Group heading="Дії" className={styles.group}>
          <Command.Item
            value="новий чат new chat"
            className={styles.item}
            onSelect={() => {
              onNewChat();
              close();
            }}
          >
            <PlusOutlined />
            <span>Новий чат</span>
          </Command.Item>
        </Command.Group>

        <Command.Group heading="Команди" className={styles.group}>
          {SLASH_COMMANDS.map((c) => (
            <Command.Item
              key={c.keyword}
              value={`${c.keyword} ${c.label}`}
              className={styles.item}
              onSelect={() => {
                onRunPrompt(c.prompt);
                close();
              }}
            >
              <ThunderboltOutlined />
              <span>{c.label}</span>
              <code className={styles.kbd}>{c.keyword}</code>
            </Command.Item>
          ))}
        </Command.Group>

        {conversations.length > 0 && (
          <Command.Group heading="Розмови" className={styles.group}>
            {conversations.map((conv) => (
              <Command.Item
                key={conv.id}
                value={`conv-${conv.id} ${conv.title || 'Без назви'}`}
                className={styles.item}
                onSelect={() => {
                  onSelectConversation(conv.id);
                  close();
                }}
              >
                <MessageOutlined />
                <span>{conv.title || 'Без назви'}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
    </Command.Dialog>
  );
};

export default AgentCommandPalette;
